/**
 * "Last used" sign-in method, shown as a hint on the login page.
 *
 * Per browser, not per user: it lives in localStorage so it survives closing
 * the browser, and is deliberately NOT cleared on logout — the hint is most
 * useful on the login screen right after one. It holds only a method name.
 *
 * OAuth is a full-page redirect and the callback route receives only
 * `?token=`, so the provider can't be known there. The button click records it
 * as *pending* in sessionStorage (same tab survives the round-trip), and the
 * callback promotes it on success. A callback with no pending value (APUS /
 * Saleshandy S2S logins redeem tokens on the same route) records nothing.
 */
export type AuthMethod = 'google' | 'microsoft' | 'code' | 'password';

export const LAST_AUTH_METHOD_KEY = 'trulyinbox-last-auth-method';
const PENDING_AUTH_METHOD_KEY = 'trulyinbox-pending-auth-method';

const AUTH_METHODS: readonly AuthMethod[] = ['google', 'microsoft', 'code', 'password'];

function isAuthMethod(value: string | null): value is AuthMethod {
  return value !== null && (AUTH_METHODS as readonly string[]).includes(value);
}

/** Remember the OAuth provider the user is about to be redirected to. */
export function setPendingAuthMethod(method: AuthMethod): void {
  try {
    sessionStorage.setItem(PENDING_AUTH_METHOD_KEY, method);
  } catch {
    // Storage unavailable (private mode) — the hint is a convenience only.
  }
}

/**
 * Record a successful sign-in. With no argument, promotes the pending OAuth
 * method (if any); the pending value is always consumed.
 */
export function commitAuthMethod(method?: AuthMethod): void {
  try {
    const pending = sessionStorage.getItem(PENDING_AUTH_METHOD_KEY);
    sessionStorage.removeItem(PENDING_AUTH_METHOD_KEY);
    const resolved = method ?? (isAuthMethod(pending) ? pending : null);
    if (resolved) localStorage.setItem(LAST_AUTH_METHOD_KEY, resolved);
  } catch {
    // Storage unavailable — nothing to remember.
  }
}

export function getLastAuthMethod(): AuthMethod | null {
  try {
    const value = localStorage.getItem(LAST_AUTH_METHOD_KEY);
    return isAuthMethod(value) ? value : null;
  } catch {
    return null;
  }
}
